import { useNavigate, Link } from 'react-router-dom';
import { Activity } from '@/services/activityService';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle2, User, FileText, AlertCircle, ExternalLink, Play, XCircle, Upload, ClipboardList, Files } from 'lucide-react';
import { formatItalianDate, formatItalianDateTime } from '@/utils/date';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AssignableUser {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface ActivityCardProps {
  activity: Activity;
  users?: AssignableUser[];
  onAssign?: (userId: string) => void;
  onDetailClick?: () => void;
  showCheckbox?: boolean;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  showStatusActions?: boolean;
  onStatusChange?: (status: Activity['status']) => void;
  actionType?: 'file' | 'form' | null;
  onActionClick?: () => void;
  className?: string;
}

// Mapping per tipi legacy - activity.type è stringa (codice da activity_types)
const activityTypeLabels: Record<string, string> = {
  CALL: 'Chiamata',
  EMAIL: 'Email',
  MEETING: 'Riunione',
  DOCUMENT_REVIEW: 'Revisione Documento',
  SITE_VISIT: 'Sopralluogo',
  FILING: 'Archiviazione',
  PAYMENT: 'Pagamento',
  FOLLOW_UP: 'Follow-up',
  OTHER: 'Altro',
};

const activityTypeIcons: Record<string, typeof Clock> = {
  CALL: Clock,
  EMAIL: FileText,
  MEETING: AlertCircle,
  DOCUMENT_REVIEW: FileText,
  SITE_VISIT: AlertCircle,
  FILING: FileText,
  PAYMENT: CheckCircle2,
  FOLLOW_UP: Clock,
  OTHER: Clock,
};

const statusColors: Record<Activity['status'], string> = {
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 border-blue-300',
  COMPLETED: 'bg-green-100 text-green-800 border-green-300',
  CANCELLED: 'bg-gray-100 text-gray-800 border-gray-300',
};

const statusLabels: Record<Activity['status'], string> = {
  PENDING: 'In Attesa',
  IN_PROGRESS: 'In Corso',
  COMPLETED: 'Completata',
  CANCELLED: 'Annullata',
};

export default function ActivityCard({
  activity,
  users = [],
  onAssign,
  onDetailClick,
  showCheckbox = false,
  selected = false,
  onSelectChange,
  showStatusActions = false,
  onStatusChange,
  actionType,
  onActionClick,
  className,
}: ActivityCardProps) {
  const navigate = useNavigate();
  const TypeIcon = activityTypeIcons[activity.type] || Clock;
  const typeLabel = activityTypeLabels[activity.type] || activity.type;
  const isOverdue = activity.dueDate && new Date(activity.dueDate) < new Date() && activity.status !== 'COMPLETED' && activity.status !== 'CANCELLED';
  const daysUntilDue = activity.dueDate
    ? Math.ceil((new Date(activity.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const handleDetailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDetailClick) {
      onDetailClick();
    } else {
      navigate(`/activities/${activity.id}`);
    }
  };

  const handleAssignChange = (value: string) => {
    if (!onAssign) return;
    if (value && value !== '_none_') {
      onAssign(value);
    } else {
      onAssign('');
    }
  };

  const userOptions = [
    { value: '_none_', label: 'Non assegnata' },
    ...users.map((u) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName}`,
    })),
  ];

  return (
    <Card
      className={cn(
        'transition-all hover:shadow-md',
        isOverdue && 'border-red-300 bg-red-50',
        className
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {showCheckbox && onSelectChange && (
            <div className="flex items-center pt-1" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => onSelectChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <TypeIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <h3 className="font-semibold text-gray-900 truncate">{activity.title}</h3>
              <button
                type="button"
                onClick={handleDetailClick}
                className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex-shrink-0"
                title="Apri dettaglio"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-2">
              {activity.practice?.practiceNumber && (
                <span className="text-xs font-mono text-muted-foreground" title="Numero pratica">
                  #{activity.practice.practiceNumber}
                </span>
              )}
              <Link
                to={`/practices/${activity.practiceId}?tab=documents`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-xs font-medium text-foreground hover:bg-accent"
                title="Scheda pratica — Documenti"
              >
                <Files className="h-3 w-3" />
                Documenti
              </Link>
            </div>

            {activity.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{activity.description}</p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={statusColors[activity.status]}>
                {statusLabels[activity.status]}
              </Badge>

              <Badge variant="outline" className="text-xs">
                {typeLabel}
              </Badge>

              {activity.practice && (
                <Badge variant="outline" className="text-xs">
                  {activity.practice.type?.code || 'Pratica'}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              {activity.user && (
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>{activity.user.firstName} {activity.user.lastName}</span>
                </div>
              )}

              {activity.dueDate && (
                <div className={cn(
                  'flex items-center gap-1',
                  isOverdue && 'text-red-600 font-semibold'
                )}>
                  <Clock className="h-3 w-3" />
                  <span>
                    {isOverdue ? `Scaduta ${Math.abs(daysUntilDue!)} giorni fa` :
                      daysUntilDue === 0 ? 'Scade oggi' :
                        daysUntilDue && daysUntilDue > 0 ? `Scade tra ${daysUntilDue} giorni` :
                          formatItalianDate(activity.dueDate)}
                  </span>
                </div>
              )}

              {activity.completedAt && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>Completata {formatItalianDateTime(activity.completedAt)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap" onClick={(e) => e.stopPropagation()}>
            {activity.status === 'IN_PROGRESS' && actionType && onActionClick && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className={cn(
                  'h-7 gap-1 px-2 text-xs border-0',
                  actionType === 'file' ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                )}
                onClick={onActionClick}
                title={actionType === 'file' ? 'Inserisci file' : 'Compila modulo'}
              >
                {actionType === 'file' ? <Upload className="h-3 w-3" /> : <ClipboardList className="h-3 w-3" />}
                {actionType === 'file' ? 'File' : 'Modulo'}
              </Button>
            )}
            {showStatusActions && onStatusChange && activity.status !== 'COMPLETED' && activity.status !== 'CANCELLED' && (
              <div className="flex items-center gap-1">
                {activity.status === 'PENDING' && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 border-0"
                    onClick={() => onStatusChange('IN_PROGRESS')}
                    title="Accetta (mette in corso)"
                  >
                    <Play className="h-3 w-3" />
                    Accetta
                  </Button>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs bg-green-100 text-green-800 hover:bg-green-200 border-0"
                  onClick={() => onStatusChange('COMPLETED')}
                  title="Completa"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Completa
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 border-0"
                  onClick={() => onStatusChange('CANCELLED')}
                  title="Annulla"
                >
                  <XCircle className="h-3 w-3" />
                  Annulla
                </Button>
              </div>
            )}
            {users.length > 0 && onAssign && activity.status !== 'COMPLETED' && activity.status !== 'CANCELLED' && (
              <Select
                value={activity.userId || '_none_'}
                onValueChange={handleAssignChange}
              >
                <SelectTrigger className="w-[160px] h-8 text-xs">
                  <SelectValue placeholder="Assegna a..." />
                </SelectTrigger>
                <SelectContent>
                  {userOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {activity.status === 'COMPLETED' && !onAssign && (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
